// src/pages/ApplyCandidate.tsx: public candidate application (no auth required).
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { C, HEARD_ABOUT, generatePassword, sendMail } from '../lib/applications';
import { uploadCV } from '../lib/talentConnect';
import {
  CANDIDATE_TARGET_ROLES, SENIORITY_LEVELS, RELOCATION_OPTIONS, ASSET_CLASS_OPTIONS,
  MAX_CANDIDATE_SKILLS, MAX_CV_BYTES, seniorityLevelLabel, assetClassLabels,
} from '../lib/onboardingApplications';
import {
  ApplyShell, ProgressRail, SubmittedScreen, Field, Row, Section, CheckboxGroup,
  YesNo, TagInput, ErrorBanner, card, inputStyle, btnPrimary, btnGhost, stepHeading,
} from '../components/ApplyShell';

const STEPS = ['Contact Details', 'Current Position', 'What You Want', 'Background', 'Review & Submit'];

const ROLE_OPTIONS = CANDIDATE_TARGET_ROLES.map(r => ({ value: r, label: r }));
const RELOCATION_CHOICES = RELOCATION_OPTIONS.map(r => ({ value: r, label: r }));

export default function ApplyCandidate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1
  const [contactName, setContactName]   = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [heardAbout, setHeardAbout]     = useState('');

  // Step 2
  const [currentRole, setCurrentRole]         = useState('');
  const [currentEmployer, setCurrentEmployer] = useState('');
  const [hideEmployer, setHideEmployer]       = useState(false);
  const [yearsExperience, setYearsExperience] = useState('');
  const [seniority, setSeniority]             = useState('');

  // Step 3
  const [targetRoles, setTargetRoles]   = useState<string[]>([]);
  const [assetClasses, setAssetClasses] = useState<string[]>([]);
  const [skills, setSkills]             = useState<string[]>([]);
  const [openToRelocation, setOpenToRelocation] = useState(false);
  const [relocationCountries, setRelocation]    = useState<string[]>([]);

  // Step 4
  const [bio, setBio]           = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [wantsCv, setWantsCv]   = useState(false);
  const [cvFile, setCvFile]     = useState<File | null>(null);
  const [cvError, setCvError]   = useState('');

  // Step 5
  const [confirmAccurate, setConfirmAccurate] = useState(false);
  const [confirmTerms, setConfirmTerms]       = useState(false);

  const toggle = (list: string[], set: (v: string[]) => void) => (v: string) =>
    set(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  const step1Valid = contactName.trim() && contactEmail.trim();

  const pickCv = (file: File | null) => {
    setCvError('');
    if (!file) { setCvFile(null); return; }
    if (file.type !== 'application/pdf') { setCvError('Please upload a PDF.'); return; }
    if (file.size > MAX_CV_BYTES) { setCvError('That file is over 5MB.'); return; }
    setCvFile(file);
  };

  const next = () => { setError(''); setStep(s => Math.min(5, s + 1)); };
  const back = () => { setError(''); setStep(s => Math.max(1, s - 1)); };

  const submit = async () => {
    setSubmitting(true); setError('');
    const email = contactEmail.trim().toLowerCase();
    try {
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email,
        password: generatePassword(),
        options: {
          data: { full_name: contactName.trim(), role: 'candidate' },
          emailRedirectTo: `${window.location.origin}/application-status?type=candidate`,
        },
      });
      if (signUpErr && !/already registered/i.test(signUpErr.message)) throw signUpErr;

      const profileId = signUpData?.user?.id
        || (await supabase.auth.getUser()).data.user?.id
        || null;
      if (!profileId) {
        throw new Error('This email already has an account. Please sign in first, then reapply.');
      }

      // A CV upload failure must not lose the application.
      let cvPath: string | null = null;
      if (wantsCv && cvFile) {
        try {
          cvPath = await uploadCV(profileId, cvFile);
        } catch (e) {
          console.error('CV upload failed:', e);
        }
      }

      const { error: appErr } = await supabase.from('candidate_applications').insert({
        contact_name: contactName.trim(),
        contact_email: email,
        contact_phone: contactPhone.trim() || null,
        heard_about: heardAbout || null,
        current_role: currentRole.trim() || null,
        current_employer: currentEmployer.trim() || null,
        hide_employer: hideEmployer,
        years_experience: yearsExperience ? parseInt(yearsExperience) : null,
        seniority: seniority || null,
        target_roles: targetRoles,
        asset_classes: assetClasses,
        skills,
        relocation_countries: openToRelocation ? relocationCountries : [],
        open_to_relocation: openToRelocation,
        bio: bio.trim() || null,
        linkedin_url: linkedin.trim() || null,
        cv_url: cvPath,
        status: 'submitted',
        profile_id: profileId,
      });
      if (appErr) throw appErr;

      await sendMail('candidate_application_received', {
        contact_email: email, contact_name: contactName.trim(),
        current_role: currentRole.trim(),
        seniority: seniorityLevelLabel(seniority),
        target_roles: targetRoles.join(', '),
      });

      setSubmitted(true);
    } catch (e: any) {
      setError(e?.message || 'Could not submit your application. Please try again.');
    }
    setSubmitting(false);
  };

  const shell = (children: React.ReactNode) => (
    <ApplyShell
      title="Apply as a Candidate"
      subtitle="Find your next role in alternative investments"
      badge="Free to Join"
    >{children}</ApplyShell>
  );

  if (submitted) return shell(
    <SubmittedScreen
      email={contactEmail}
      timeline="We will review your application and activate your account within 1 business day."
      onContinue={() => navigate('/application-status?type=candidate')}
    />
  );

  return shell(
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '28px 24px 64px' }}>
      <ProgressRail steps={STEPS} step={step} />
      {error && <ErrorBanner text={error} />}

      {/* ── Step 1 ── */}
      {step === 1 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Contact details</h2>
          <Field label="Full name *">
            <input style={inputStyle} value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Jane Smith" />
          </Field>
          <Field label="Email address *">
            <input style={inputStyle} type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="jane@example.com" />
          </Field>
          <Field label="Phone number (optional)">
            <input style={inputStyle} value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+44 20 1234 5678" />
          </Field>
          <Field label="How did you hear about us?">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={heardAbout} onChange={e => setHeardAbout(e.target.value)}>
              <option value="">Select…</option>
              {HEARD_ABOUT.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px' }}>
            <button style={{ ...btnPrimary, opacity: step1Valid ? 1 : 0.5 }} disabled={!step1Valid} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 2 ── */}
      {step === 2 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Current position</h2>
          <Field label="Current job title">
            <input style={inputStyle} value={currentRole} onChange={e => setCurrentRole(e.target.value)} placeholder="Senior Analyst" />
          </Field>
          <Field label="Current employer">
            <input style={inputStyle} value={currentEmployer} onChange={e => setCurrentEmployer(e.target.value)} placeholder="Meridian Capital" />
          </Field>
          <Field label="Hide employer on your profile?"
            hint="When on, managers see your role but not the name of your current employer.">
            <YesNo value={hideEmployer} onChange={setHideEmployer} yesLabel="Hide it" noLabel="Show it" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '14px' }}>
            <Field label="Years of experience">
              <input style={inputStyle} type="number" min="0" value={yearsExperience} onChange={e => setYearsExperience(e.target.value)} placeholder="8" />
            </Field>
            <Field label="Seniority level">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={seniority} onChange={e => setSeniority(e.target.value)}>
                <option value="">Select…</option>
                {SENIORITY_LEVELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={btnPrimary} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 3 ── */}
      {step === 3 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>What you are looking for</h2>
          <Field label="Target roles">
            <CheckboxGroup options={ROLE_OPTIONS} selected={targetRoles} onToggle={toggle(targetRoles, setTargetRoles)} columns={2} />
          </Field>
          <Field label="Asset classes of interest">
            <CheckboxGroup options={ASSET_CLASS_OPTIONS} selected={assetClasses} onToggle={toggle(assetClasses, setAssetClasses)} columns={3} />
          </Field>
          <Field label="Key skills" hint={`Type a skill and press Enter. Up to ${MAX_CANDIDATE_SKILLS}.`}>
            <TagInput tags={skills} onChange={setSkills} max={MAX_CANDIDATE_SKILLS} placeholder="e.g. Python, Credit Analysis" />
          </Field>
          <Field label="Open to relocation?">
            <YesNo value={openToRelocation} onChange={setOpenToRelocation} />
          </Field>
          {openToRelocation && (
            <Field label="Countries you would relocate to">
              <CheckboxGroup options={RELOCATION_CHOICES} selected={relocationCountries} onToggle={toggle(relocationCountries, setRelocation)} columns={2} />
            </Field>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={btnPrimary} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 4 ── */}
      {step === 4 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Background</h2>
          <Field label="Brief bio"
            hint="Tell managers about your background, experience and what makes you a strong candidate. 100-500 words works well.">
            <textarea style={{ ...inputStyle, height: '150px', resize: 'vertical', lineHeight: 1.6 }}
              value={bio} onChange={e => setBio(e.target.value)} />
            <div style={{ fontSize: '11px', color: C.mutedLt, marginTop: '5px' }}>
              {bio.trim() ? bio.trim().split(/\s+/).length : 0} words
            </div>
          </Field>
          <Field label="LinkedIn URL (optional)">
            <input style={inputStyle} value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="linkedin.com/in/..." />
          </Field>
          <Field label="Would you like to upload your CV?"
            hint="Your CV is only shared with managers you connect with.">
            <YesNo value={wantsCv} onChange={v => { setWantsCv(v); if (!v) { setCvFile(null); setCvError(''); } }} />
          </Field>
          {wantsCv && (
            <Field label="CV (PDF, max 5MB)">
              <input type="file" accept="application/pdf" style={{ ...inputStyle, padding: '7px 9px' }}
                onChange={e => pickCv(e.target.files?.[0] || null)} />
              {cvError && <div style={{ fontSize: '12px', color: C.danger, marginTop: '6px' }}>{cvError}</div>}
              {cvFile && !cvError && (
                <div style={{ fontSize: '12px', color: C.success, marginTop: '6px' }}>Selected: {cvFile.name}</div>
              )}
            </Field>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={btnPrimary} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 5 ── */}
      {step === 5 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Review and submit</h2>

          <Section title="Contact">
            <Row k="Name" v={contactName} />
            <Row k="Email" v={contactEmail} />
            <Row k="Phone" v={contactPhone} />
          </Section>

          <Section title="Current position">
            <Row k="Job title" v={currentRole} />
            <Row k="Employer" v={currentEmployer ? `${currentEmployer}${hideEmployer ? ' (hidden on profile)' : ''}` : ''} />
            <Row k="Years of experience" v={yearsExperience} />
            <Row k="Seniority" v={seniorityLevelLabel(seniority)} />
          </Section>

          <Section title="Looking for">
            <Row k="Target roles" v={targetRoles.join(', ')} />
            <Row k="Asset classes" v={assetClassLabels(assetClasses)} />
            <Row k="Skills" v={skills.join(', ')} />
            <Row k="Relocation" v={openToRelocation ? (relocationCountries.join(', ') || 'Yes') : 'No'} />
          </Section>

          <Section title="Background">
            <Row k="Bio" v={bio.trim() ? `${bio.trim().slice(0, 120)}${bio.trim().length > 120 ? '…' : ''}` : ''} />
            <Row k="LinkedIn" v={linkedin} />
            <Row k="CV" v={cvFile ? cvFile.name : 'Not provided'} />
          </Section>

          <div style={{ marginTop: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
              <input type="checkbox" checked={confirmAccurate} onChange={e => setConfirmAccurate(e.target.checked)} style={{ marginTop: '2px' }} />
              I confirm that all information provided is accurate
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
              <input type="checkbox" checked={confirmTerms} onChange={e => setConfirmTerms(e.target.checked)} style={{ marginTop: '2px' }} />
              <span>
                I agree to the Altveno{' '}
                <a href="/terms" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Terms of Service</a>{' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Privacy Policy</a>
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '22px' }}>
            <button style={btnGhost} onClick={back} disabled={submitting}>Back</button>
            <button
              style={{ ...btnPrimary, opacity: confirmAccurate && confirmTerms && !submitting ? 1 : 0.5 }}
              disabled={!confirmAccurate || !confirmTerms || submitting}
              onClick={submit}
            >{submitting ? 'Submitting…' : 'Submit application'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
